import logEventData from './lib/event-stream-debug-logger';
import executeWindowEvent from './lib/window-event-handler';
import pushEvent from './lib/analytics-event-handler';
import ctx from './util/qna-context';
import extractStateObjectProps from './util/extract-state-props';
import './schema/lex-state-object';

/**
 * SP ADDITION
 * handles everything that needs to happen on 'updatelexstate'
 * @date 2023-07-27
 * @author Joem Elias Sanez @israelias
 *
 */

/**
 * @typedef {{
 *   app_instance_id: string,
 *   app_instance_time: string,
 *   botName: string,
 *   botId: string,
 *   call_session_id: string | null,
 *   direction: string,
 *   module: string,
 *   module_title: string,
 *   page: {
 *     url?: string,
 *   },
 *   permanent_id: string,
 *   referrer: {
 *     url?: string,
 *   },
 *   reply: string,
 *   session_id: string,
 *   user_agent: string,
 *   user_email: string | null,
 * }} BotInteractionV2Props
 */

const IS_DEBUG = process.env.NODE_ENV === 'development';

const selectedOption = (rcb, o) => rcb.find(rc => rc.value.trim().replace('QID::', '').toLowerCase() === o.trim().replace('QID::', '').toLowerCase()) || { text: ' ', value: ' ' };
/**
 * The onUpdateLexState function is a listener for the 'lex-state-updated' event.
 * It listens for updates to the Lex state and
 *
 * 1. triggers a loadBehind event
 * 2. sends evt.* to analytics
 *
 * @param {{
 *   evt: {
 *     detail: LexStateObject
 *   }
 * }} evt - Pass in the event object
 */
const onUpdateLexState = (evt) => {
  if (evt && 'detail' in evt && evt.detail && 'state' in evt.detail) {
    IS_DEBUG && logEventData(evt);

    const {
      session_id,
      dbNav,
      dbNavTitle,
      message,
      resCardButtons,
      qnabot_qid,
      q,
      qid,
    } = extractStateObjectProps(evt);

    ctx.changeInitialModule = sessionStorage.getItem('initialModule') || '';
    ctx.changeSelected = selectedOption(ctx.getSelections, q);


    /**
     * Analytics Getters
     * for IN-coming
     */
    // ctx.changeReply = q === ctx.getInitialModule && ctx.getSelected.value === ' ' ? 'Restart' : ctx.getSelected.text;
    ctx.changeReply = ctx.getModule && ctx.getSessionId !== session_id ? 'Restart' : ctx.getSelected.text;
    ctx.changeButtonLabel = q === ctx.getInitialModule ? ' ' : ctx.getSelected.text;
    ctx.changeSessionId = session_id;

    /**
     * Analytics IN event
     * @todo test table to be created
     */
    pushEvent({
      category: 'botInteraction',
      data: {
        direction: 'in',
        module_title: ctx.getModuleTitle,
        module: ctx.getModule,
        buttonLabel: ctx.getButtonLabel,
        reply: ctx.getReply,
        session_id,
      },
    })
      .catch((err) => {
        console.error('Failed to push incoming data to bq', err);
      });

    IS_DEBUG && console.log(JSON.stringify({
      direction: 'in',
      module_title: ctx.getModuleTitle,
      module: ctx.getModule,
      buttonLabel: ctx.getButtonLabel,
      reply: ctx.getReply,
      session_id,
    }, null, 2));

    /**
     * LoadBehind event
     */
    executeWindowEvent(dbNav, dbNavTitle);

    /**
     * Analytics Setters
     * for OUT-going
     */
    ctx.changeModuleTitle = qnabot_qid;
    ctx.changeModule = q;
    ctx.changeReply = message;
    ctx.changeSelections = resCardButtons;
    /**
     * Analytics OUT event
     * @todo test table to be created
     */
    pushEvent({
      category: 'botInteraction',
      data: {
        direction: 'out',
        module_title: ctx.getModuleTitle,
        module: ctx.getModule,
        reply: ctx.getReply,
        session_id,
      },
    })
      .catch((err) => {
        console.error('Failed to push outgoing event data to bq', err);
      });

    IS_DEBUG && console.log(JSON.stringify({
      direction: 'out',
      module_title: ctx.getModuleTitle,
      module: ctx.getModule,
      reply: ctx.getReply,
      session_id,
    }, null, 2));
  }
};

/**
 * The registerEventListeners function registers event listeners for the following events:
 * - updatelexstate
 * @returns Undefined
 */
const registerEventListeners = (config) => {
  IS_DEBUG && console.log('config iframe load', config);
  const customerName = config.ui.pageTitle;
  const botName = config.lex.botName;
  const stage = customerName.includes('staging') || botName.includes('staging') ? 'staging' : 'prod';
  const botId = customerName.includes('staging') ? config.lex.v2BotId : config.lex.v2BotAliasId;
  console.log(`mapping bot_id ${botId} and bot_name ${botName} for ${customerName} in ${stage} environment`);

  sessionStorage.setItem('bot_name', botName);
  sessionStorage.setItem('bot_id', botId);
  console.log('setting initial module', config.lex.initialUtterance);
  sessionStorage.setItem('initialModule', config.lex.initialUtterance);
  console.log('registering event listeners');

  window.addEventListener('updatelexstate', onUpdateLexState, true);
};

export default registerEventListeners;
